import React, { useState, useEffect } from 'react';

import {
  Add, 
} from "@mui/icons-material";
import { Tooltip, Fab } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { 
  ModelBase 
} from "@models";
import { 
  SpellSlotType, 
  SpellSlotTableEntry,
} from "@dndModels";
import { CHARACTER_LEVELS } from "@dndModels/Constants";

import API from "@utilities/smart_api";
import { makeStyles } from "@utilities/theme_utilities";

import StringBox from "@components/input/strings/StringBox";

import SelectStringBox from "@components/input/selects/SelectStringBox";

import ModelBaseInput from "@dndComponents/ModelBaseInput";
import DeleteButton from "@components/input/buttons/icon_buttons/DeleteButton";

const api = API.getInstance();

interface Props {
  obj?: ModelBase;
}

export default function SpellSlotTypeEdit({ obj: param_obj }: Props) {
  const [reloading, setReloading] = useState(false);
  const [ping, setPing] = useState(false);
  const [obj, setObj] = useState<SpellSlotType>(param_obj instanceof SpellSlotType ? param_obj : api.applyWorkingOn(new SpellSlotType()));
  
  const disabled = api.user === null || !api.online || (!api.user.admin && obj._id !== "" && obj.owner_id !== api.user._id);
    
  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading, setReloading]);
  
  const useRowStyles = makeStyles()((theme) => {
    return ({
      root: {
        backgroundColor: theme.palette.secondary.main,
        color: "white"
      },
    });
  });

  const onAdd = () => {
    const new_obj = obj;
    const entry = new SpellSlotTableEntry();
    if (new_obj.entries.length > 0) {
      const previous = new_obj.entries[new_obj.entries.length - 1];
      entry.level = previous.level + 1;
      entry.slots_per_level = {...previous.slots_per_level};
    } else {
      entry.level = 1;
    }
    new_obj.entries.push(entry);
    setObj(new_obj);
    setPing(!ping);
  }

  const renderEntrySpellSlotBoxes = (entry: SpellSlotTableEntry) => {
    const return_me: any = [];
    for (let i = 1; i < 10; ++i) {
      return_me.push(renderEntrySpellSlotBox(entry, i));
    }
    return return_me;
  }

  const renderEntrySpellSlotBox = (entry: SpellSlotTableEntry, slot_level: number) => {
    // If I'm going to change it to allow for different numbers of slots then I'd have to change it here.
    const value = entry.slots_per_level[slot_level] ? `${entry.slots_per_level[slot_level]}` : " - ";
    return (
      <Grid
        key={slot_level}
        style={{ backgroundColor: (value === " - " ? "black" : "inherit") }}
        size={{
          xs: 1
        }}>
        <SelectStringBox 
          options={[" - ","1","2","3","4","5","6"]}
          value={ value } 
          name={`${slot_level}`}
          disabled={disabled}
          onChange={(changed: string) => {
            if (changed === " - ") {
              delete entry.slots_per_level[slot_level];
            } else {
              entry.slots_per_level[slot_level] = +changed;
            }
            setPing(!ping);
          }}
        />
      </Grid>
    );
  }
  
  const { classes: rowClasses, cx: cx2 } = useRowStyles();
  return (
    <ModelBaseInput 
      obj={obj}
      disabled={disabled}
      onChange={(changed: ModelBase) => {
        if (changed instanceof SpellSlotType) {
          const new_obj = obj;
          new_obj.copy(changed);
          setObj(new_obj);
        }
      }}>
      <Grid container spacing={0} direction="row">
        <Grid
          size={{
            xs: 12
          }}>
          <StringBox 
            value={obj.slot_name} 
            name="Slot Name"
            disabled={disabled}
            onBlur={(value: string) => {
              const new_obj = obj;
              new_obj.slot_name = value;
              setObj(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <SelectStringBox 
            value={obj.refresh_rule} 
            options={["Long Rest","Short Rest"]}
            name="Refresh Rule"
            disabled={disabled}
            onChange={(value: "Short Rest" | "Long Rest") => {
              const new_obj = obj;
              new_obj.refresh_rule = value;
              setObj(new_obj);
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12
          }}>
          <span className="header">
            Entries
          </span>
          { !disabled &&
            <Tooltip title={`Create New Spellcasting Level`}>
              <Fab size="small" color="primary" style={{marginLeft: "8px"}}
                onClick={onAdd}>
                <Add/>
              </Fab>
            </Tooltip>
          }
        </Grid>
        { obj.entries.map((entry, key) => {
          return (
            <Grid
              key={key}
              className="padded"
              size={{
                xs: 12
              }}>
              <Grid container spacing={0} direction="row"
                className={cx2(rowClasses.root)}>
                <Grid
                  size={{
                    xs: 2
                  }}>
                  <SelectStringBox 
                    name="Level"
                    value={`${entry.level}`} 
                    options={CHARACTER_LEVELS}
                    disabled={disabled}
                    onChange={(value: string) => {
                      entry.level = +value;
                      setPing(!ping);
                    }}
                  />
                </Grid>
                { renderEntrySpellSlotBoxes(entry) }
                <Grid
                  size={{
                    xs: 1
                  }}>
                  <DeleteButton
                    disabled={disabled}
                    onClick={() => {
                      const new_obj = obj;
                      new_obj.entries = new_obj.entries.filter(o => o.level !== entry.level);
                      setObj(new_obj);
                      setPing(!ping);
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          );
        })}
      </Grid>
    </ModelBaseInput>
  );
}